import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Wallet, PiggyBank, Landmark, TrendingUp, Crown } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/lib/format";
import { Avatar } from "@/components/Avatar";

const Tile = ({ icon: Icon, hi, value, tone }) => (
  <div className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100">
    <div className={`inline-flex p-2 rounded-xl mb-2 ${tone}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-lg font-mono font-bold">{value}</p>
    <p className="text-xs font-display font-semibold text-muted-foreground">{hi}</p>
  </div>
);

const Card = ({ title, children, testid }) => (
  <div className="rounded-2xl bg-white p-4 shadow-sm border border-slate-100" data-testid={testid}>
    <p className="font-display font-bold mb-3">{title}</p>
    {children}
  </div>
);

export default function ReportsScreen() {
  const { member } = useAuth();
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get("/reports").then((res) => setR(res.data));
  }, []);

  if (!r) return <p className="text-center text-muted-foreground py-10">लोड हो रहा है…</p>;

  const pieData = [
    { name: "नियमित", value: r.regular, color: "#10b981" },
    { name: "डिफॉल्टर", value: r.defaulter, color: "#ef4444" },
  ];
  const loanData = [
    { name: "दिया", amount: r.loan_disbursed },
    { name: "वसूल", amount: r.loan_recovered },
    { name: "बाकी", amount: r.loan_outstanding },
  ];

  return (
    <div className="space-y-4" data-testid="reports-screen">
      <h2 className="font-display font-bold text-2xl text-center text-primary">रिपोर्ट / Reports</h2>

      <div className="grid grid-cols-2 gap-3">
        <Tile icon={Wallet} hi="कुल पूल / Pool" value={inr(r.total_pool)} tone="bg-blue-100 text-blue-700" />
        <Tile icon={PiggyBank} hi="कुल बचत / Savings" value={inr(r.total_savings)} tone="bg-emerald-100 text-emerald-700" />
        <Tile icon={Landmark} hi="ऋण बाकी / Outstanding" value={inr(r.loan_outstanding)} tone="bg-red-100 text-red-700" />
        <Tile icon={TrendingUp} hi="वसूली / Recovery" value={`${r.recovery_pct}%`} tone="bg-indigo-100 text-indigo-700" />
      </div>

      <Card title="मासिक योगदान / Monthly Contribution" testid="chart-monthly">
        <div className="h-48 w-full" style={{ minHeight: 192 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={r.monthly_contribution} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 10, fontSize: 12, border: "1px solid #e2e8f0" }} />
              <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        <Card title="सदस्य वर्ग / Member Category" testid="chart-category">
          <div className="h-48 w-full" style={{ minHeight: 192 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="ऋण सारांश / Loan Summary" testid="chart-loan">
          <div className="h-48 w-full" style={{ minHeight: 192 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={loanData} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="amount" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {r.top_contributors && (
        <Card title="शीर्ष योगदानकर्ता / Top Contributors" testid="top-contributors">
          <div className="space-y-2">
            {r.top_contributors.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3">
                <span className="w-5 text-sm font-bold text-muted-foreground">{i + 1}</span>
                <Avatar name={c.name} size={36} />
                <span className="flex-1 text-sm font-medium flex items-center gap-1">
                  {c.name} {i === 0 && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                </span>
                <span className="font-mono font-semibold text-sm">{inr(c.savings)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
